import { Injectable, inject } from '@angular/core';

import { ScoringSession } from '@core/models/brijjsports.models';
import { BrijjSportsStateService } from '@core/state/brijjsports-state.service';

import { ScoringAdapter } from '../sport.types';
import { SportDashboardTile } from '../sport.types';
import { BasketballGame, BoxScoreLine, PLAY_POINTS, PlayEvent, emptyBoxLine } from './basketball.models';

@Injectable({ providedIn: 'root' })
export class BasketballScoringAdapter implements ScoringAdapter {
  private readonly state = inject(BrijjSportsStateService);

  athleteTotalInSession(session: ScoringSession, athleteId: string): number {
    const game = this.state.basketballGameBySession(session.id);
    if (!game) return 0;
    const fromPlays = (game.plays ?? [])
      .filter(p => p.athleteId === athleteId && p.team === 'home')
      .reduce((sum, p) => sum + PLAY_POINTS[p.kind], 0);
    if (fromPlays > 0) return fromPlays;
    return game.lines.filter(l => l.athleteId === athleteId).reduce((sum, l) => sum + l.points, 0);
  }

  athleteIdsInSession(session: ScoringSession): string[] {
    if (session.participantIds?.length) return session.participantIds;
    const game = this.state.basketballGameBySession(session.id);
    if (!game) return [];
    const fromPlays = (game.plays ?? []).map(p => p.athleteId);
    const fromLines = game.lines.map(l => l.athleteId);
    return Array.from(new Set([...fromPlays, ...fromLines]));
  }

  sessionSortKey(session: ScoringSession): number {
    return new Date(session.startedAt ?? session.scheduledFor ?? session.date).getTime();
  }

  sessionLabel(session: ScoringSession): string {
    if (session.name) return session.name;
    const game = this.state.basketballGameBySession(session.id);
    if (game?.opponent) return `vs ${game.opponent}`;
    return 'Basketball game';
  }

  bestForAthlete(sessions: ScoringSession[], athleteId: string): number {
    return sessions
      .filter(s => s.sportKey === 'basketball')
      .reduce((max, s) => Math.max(max, this.athleteTotalInSession(s, athleteId)), 0);
  }

  /** Total team points by side from the play log. */
  teamScore(game: BasketballGame, side: 'home' | 'away'): number {
    return (game.plays ?? []).filter(p => p.team === side).reduce((sum, p) => sum + PLAY_POINTS[p.kind], 0);
  }

  statTiles(session: ScoringSession): SportDashboardTile[] {
    const game = this.state.basketballGameBySession(session.id);
    if (!game) return [];
    return [
      { label: 'Home', value: this.teamScore(game, 'home') },
      { label: 'Away', value: this.teamScore(game, 'away') },
      { label: 'Plays', value: game.plays?.length ?? 0 }
    ];
  }

  /** Per-player aggregated box score (home roster only) from the play log. */
  boxScore(game: BasketballGame): BoxScoreLine[] {
    const map = new Map<string, BoxScoreLine>();
    const ensure = (id: string): BoxScoreLine => {
      let row = map.get(id);
      if (!row) {
        row = emptyBoxLine(id);
        map.set(id, row);
      }
      return row;
    };
    for (const p of game.plays ?? []) {
      if (p.team !== 'home') continue;
      this.applyPlayToRow(ensure(p.athleteId), p);
    }
    return Array.from(map.values()).sort((a, b) => b.points - a.points);
  }

  /** Convenience: roll up a player's stats across all quarters of a session. */
  athleteStatLine(
    session: ScoringSession,
    athleteId: string
  ): {
    points: number;
    assists: number;
    rebounds: number;
    steals: number;
    blocks: number;
    fouls: number;
    minutesPlayed: number;
  } {
    const empty = { points: 0, assists: 0, rebounds: 0, steals: 0, blocks: 0, fouls: 0, minutesPlayed: 0 };
    const game = this.state.basketballGameBySession(session.id);
    if (!game) return empty;
    if (game.plays && game.plays.length) {
      const row = emptyBoxLine(athleteId);
      for (const p of game.plays.filter(pl => pl.athleteId === athleteId && pl.team === 'home')) {
        this.applyPlayToRow(row, p);
      }
      return {
        points: row.points,
        assists: row.ast,
        rebounds: row.rebTotal,
        steals: row.stl,
        blocks: row.blk,
        fouls: row.pf,
        minutesPlayed: 0
      };
    }
    return game.lines
      .filter(l => l.athleteId === athleteId)
      .reduce<typeof empty>(
        (sum, l) => ({
          points: sum.points + l.points,
          assists: sum.assists + l.assists,
          rebounds: sum.rebounds + l.rebounds,
          steals: sum.steals + l.steals,
          blocks: sum.blocks + l.blocks,
          fouls: sum.fouls + l.fouls,
          minutesPlayed: sum.minutesPlayed + l.minutesPlayed
        }),
        empty
      );
  }

  private applyPlayToRow(row: BoxScoreLine, p: PlayEvent): void {
    switch (p.kind) {
      case '2pt_made':
        row.fgMade += 1;
        row.fgAtt += 1;
        row.points += 2;
        break;
      case '2pt_miss':
        row.fgAtt += 1;
        break;
      case '3pt_made':
        row.fgMade += 1;
        row.fgAtt += 1;
        row.threePtMade += 1;
        row.threePtAtt += 1;
        row.points += 3;
        break;
      case '3pt_miss':
        row.fgAtt += 1;
        row.threePtAtt += 1;
        break;
      case 'ft_made':
        row.ftMade += 1;
        row.ftAtt += 1;
        row.points += 1;
        break;
      case 'ft_miss':
        row.ftAtt += 1;
        break;
      case 'reb_off':
        row.rebOff += 1;
        row.rebTotal += 1;
        break;
      case 'reb_def':
        row.rebDef += 1;
        row.rebTotal += 1;
        break;
      case 'ast':
        row.ast += 1;
        break;
      case 'stl':
        row.stl += 1;
        break;
      case 'blk':
        row.blk += 1;
        break;
      case 'foul_personal':
      case 'foul_technical':
        row.pf += 1;
        break;
      case 'sub_in':
      case 'sub_out':
        break;
    }
  }
}
